import { useCallback } from "react";
import type { GitPullResult, GitStatusResult } from "@cmmd-forge/contracts";

import type { GitActionMenuItem, GitQuickAction } from "./GitActionsControl.logic";
import type { RunGitActionWithToastInput } from "./GitActionsControl.runner";
import { toastManager, type ThreadToastData } from "~/components/ui/toast";
import { openInPreferredEditor } from "~/editorPreferences";
import { readLocalApi } from "~/localApi";
import { resolvePathLinkTarget } from "~/terminal-links";

interface UseGitActionsControlHandlersInput {
  readonly gitCwd: string | null;
  readonly gitStatus: GitStatusResult | null;
  readonly quickAction: GitQuickAction;
  readonly threadToastData: ThreadToastData | undefined;
  readonly pullMutation: { readonly mutateAsync: () => Promise<GitPullResult> };
  readonly runGitActionWithToast: (input: RunGitActionWithToastInput) => Promise<void>;
  readonly openCommitDialog: () => void;
}

export function useGitActionsControlHandlers(input: UseGitActionsControlHandlersInput) {
  const openExistingPr = useCallback(async () => {
    const api = readLocalApi();
    if (!api) {
      toastManager.add({
        type: "error",
        title: "Link opening is unavailable.",
        data: input.threadToastData,
      });
      return;
    }

    const prUrl = input.gitStatus?.pr?.state === "open" ? input.gitStatus.pr.url : null;
    if (!prUrl) {
      toastManager.add({
        type: "error",
        title: "No open PR found.",
        data: input.threadToastData,
      });
      return;
    }

    void api.shell.openExternal(prUrl).catch((error: unknown) => {
      toastManager.add({
        type: "error",
        title: "Unable to open PR link",
        description: error instanceof Error ? error.message : "An error occurred.",
        data: input.threadToastData,
      });
    });
  }, [input.gitStatus, input.threadToastData]);

  const runQuickAction = useCallback(() => {
    if (input.quickAction.kind === "open_pr") {
      void openExistingPr();
      return;
    }
    if (input.quickAction.kind === "run_pull") {
      runPullWithToast(input);
      return;
    }
    if (input.quickAction.kind === "show_hint") {
      toastManager.add({
        type: "info",
        title: input.quickAction.label,
        description: input.quickAction.hint,
        data: input.threadToastData,
      });
      return;
    }
    if (input.quickAction.action) {
      void input.runGitActionWithToast({ action: input.quickAction.action });
    }
  }, [input, openExistingPr]);

  const openMenuItem = useCallback(
    (item: GitActionMenuItem) => {
      if (item.disabled) return;
      if (item.kind === "open_pr") {
        void openExistingPr();
        return;
      }
      if (item.dialogAction === "push") {
        void input.runGitActionWithToast({ action: "push" });
        return;
      }
      if (item.dialogAction === "create_pr") {
        void input.runGitActionWithToast({ action: "create_pr" });
        return;
      }
      input.openCommitDialog();
    },
    [input, openExistingPr],
  );

  const openChangedFile = useCallback(
    (filePath: string) => {
      const api = readLocalApi();
      if (!api || !input.gitCwd) {
        toastManager.add({
          type: "error",
          title: "Editor opening is unavailable.",
          data: input.threadToastData,
        });
        return;
      }
      const target = resolvePathLinkTarget(filePath, input.gitCwd);
      void openInPreferredEditor(api, target).catch((error) => {
        toastManager.add({
          type: "error",
          title: "Unable to open file",
          description: error instanceof Error ? error.message : "An error occurred.",
          data: input.threadToastData,
        });
      });
    },
    [input.gitCwd, input.threadToastData],
  );

  return { runQuickAction, openMenuItem, openChangedFile };
}

function runPullWithToast(input: UseGitActionsControlHandlersInput): void {
  const promise = input.pullMutation.mutateAsync();
  toastManager.promise(promise, {
    loading: { title: "Pulling...", data: input.threadToastData },
    success: (result) => ({
      title: result.status === "pulled" ? "Pulled" : "Already up to date",
      description:
        result.status === "pulled"
          ? `Updated ${result.branch} from ${result.upstreamBranch ?? "upstream"}`
          : `${result.branch} is already synchronized.`,
      data: input.threadToastData,
    }),
    error: (error) => ({
      title: "Pull failed",
      description: error instanceof Error ? error.message : "An error occurred.",
      data: input.threadToastData,
    }),
  });
  void promise.catch(() => undefined);
}
